/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useEffect } from 'react';

import { FormHandles } from '@unform/core';

import typy from 'typy';
import Button from '../../../../../../../../components/Button';

import {
  Container,
  Content,
  ButtonNormal,
  ButtonHighlighted,
  LiButtonControlsArrow,
} from './styles';
import { IServiceForm } from '../../../../../../../../interfaces/service';

interface PaginationProps {
  formRef: any | FormHandles;
  listItems: Array<any>;
  perPageProp?: number;
  goToLastPage?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChangeListPaginatedItems: (UpdatedLists: {
    newListPageActual: Array<any>;
    newListCompleteUpdated: Array<any>;
  }) => void;
  handleGoToLastPage: () => void;
}

const PaginationAndSetInputsOnComponentService: React.FC<PaginationProps> = ({
  formRef,
  listItems,
  perPageProp = 10,
  goToLastPage = false,
  onChangeListPaginatedItems,
  handleGoToLastPage,
}) => {
  const [
    listCompleteAndUpdatedOfTheServices,
    setListCompleteAndUpdatedOfTheServices,
  ] = useState<Array<IServiceForm>>([]);
  const [pageActual, setPageActual] = useState(1);
  const [buttons, setButtons] = useState<number[]>([]);

  const perPage = perPageProp;

  const state = {
    perPage,
    totalPages: Math.ceil(listItems.length / perPage), // Arredonda para cima
    maxVisibleButtons: 5,
  };

  // calculateMaxVisibleButtonsPagination
  const calculateMaxVisibleButtonsPagination = useCallback(() => {
    const { maxVisibleButtons } = state;
    let maxLeft = pageActual - Math.floor(maxVisibleButtons / 2);
    let maxRight = pageActual + Math.floor(maxVisibleButtons / 2);

    if (maxLeft < 1) {
      maxLeft = 1;
      maxRight = maxVisibleButtons;
    }

    if (maxRight > state.totalPages) {
      maxLeft = state.totalPages - (maxVisibleButtons - 1);
      maxRight = state.totalPages;

      if (maxLeft < 1) maxLeft = 1;
    }

    return { maxLeft, maxRight };
  }, [pageActual, state]);
  // Fim calculateMaxVisibleButtonsPagination

  // updateButtonsPagination
  const updateButtonsPagination = useCallback((): Array<any> => {
    const newButtons = [];
    const { maxLeft, maxRight } = calculateMaxVisibleButtonsPagination();

    for (let page = maxLeft; page <= maxRight; page += 1) {
      newButtons.push(page);
    }
    return newButtons;
  }, [calculateMaxVisibleButtonsPagination]);
  // Fim updateButtonsPagination

  // update
  const updateButtons = useCallback(() => {
    setButtons(updateButtonsPagination());
  }, [updateButtonsPagination]);
  // Fim update

  const getListServiceOfThePageActual = useCallback((): Array<any> => {
    const listServicesOfThePageActual = formRef.current.getData()
      .projectHasServices;

    return listServicesOfThePageActual;
  }, [formRef]);

  // updateListCompleteAndUpdatedOfTheServices
  const updateListCompleteAndUpdatedOfTheServices = useCallback((): void => {
    const listServicesOfThePageActual = getListServiceOfThePageActual();
    // eslint-disable-next-line prefer-const
    let listUpdated: Array<any> = [...listCompleteAndUpdatedOfTheServices];

    if (listServicesOfThePageActual && listServicesOfThePageActual.length > 0) {
      // eslint-disable-next-line array-callback-return
      listServicesOfThePageActual.map((item: any): void => {
        if (
          Object.entries(item).length > 0 &&
          typy(parseInt(item.id, 10)).isNumber
        ) {
          const indexOfTheServiceToBeUpdated = listUpdated.findIndex(
            itemFind => itemFind.id.toString() === item.id.toString(),
          );

          listUpdated[indexOfTheServiceToBeUpdated] = item;
        } else if (
          item.idNewItemPagination &&
          item.idNewItemPagination !== '' &&
          item.idNewItemPagination !== null
        ) {
          const indexOfTheServiceToBeUpdated = listUpdated.findIndex(
            itemFind =>
              itemFind.idNewItemPagination === item.idNewItemPagination,
          );

          listUpdated[indexOfTheServiceToBeUpdated] = item;
        }
      });

      setListCompleteAndUpdatedOfTheServices(listUpdated);
      updateButtons();
    }
  }, [
    getListServiceOfThePageActual,
    listCompleteAndUpdatedOfTheServices,
    updateButtons,
  ]);
  // Fim updateListCompleteAndUpdatedOfTheServices

  // updateListPaginatedItems
  const updateListPaginatedItems = useCallback((): {
    newListPageActual: Array<any>;
    newListCompleteUpdated: Array<any>;
  } => {
    const page = pageActual - 1;
    const start = page * state.perPage;
    const end = start + state.perPage;

    const paginatedItems = listCompleteAndUpdatedOfTheServices.slice(
      start,
      end,
    );

    return {
      newListPageActual: paginatedItems,
      newListCompleteUpdated: listCompleteAndUpdatedOfTheServices,
    };
  }, [listCompleteAndUpdatedOfTheServices, pageActual, state.perPage]);
  // Fim updateListPaginatedItems

  // Next items
  const next = useCallback(() => {
    if (pageActual + 1 > state.totalPages) {
      setPageActual(pageActual);
    } else {
      setPageActual(pageActual + 1);
    }
    updateListCompleteAndUpdatedOfTheServices();
  }, [pageActual, state.totalPages, updateListCompleteAndUpdatedOfTheServices]);
  // End Next Items

  // Previous items
  const prev = useCallback(() => {
    if (pageActual - 1 < 1) {
      setPageActual(pageActual);
    } else {
      setPageActual(pageActual - 1);
    }
    updateListCompleteAndUpdatedOfTheServices();
  }, [pageActual, updateListCompleteAndUpdatedOfTheServices]);
  // End Previous Items

  // GoTo items
  const goTo = useCallback(
    (page: number) => {
      if (page < 1) {
        // eslint-disable-next-line no-param-reassign
        page = 1;
      }

      setPageActual(page);

      if (page > state.totalPages) {
        setPageActual(state.totalPages);
      }
      updateListCompleteAndUpdatedOfTheServices();
    },
    [state.totalPages, updateListCompleteAndUpdatedOfTheServices],
  );
  // End GoTo items

  useEffect(() => {
    setListCompleteAndUpdatedOfTheServices(listItems);
  }, [listItems]);

  useEffect(() => {
    setButtons(updateButtonsPagination());
    const updatedLists = updateListPaginatedItems();
    onChangeListPaginatedItems(updatedLists);

    if (goToLastPage) {
      goTo(state.totalPages);
      handleGoToLastPage();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listCompleteAndUpdatedOfTheServices, pageActual]);

  return (
    <Container>
      <Content>
        <nav aria-label="Page navigation">
          <ul>
            <LiButtonControlsArrow>
              <Button onClick={() => goTo(1)}>
                <span aria-hidden="true">&laquo;</span>
              </Button>
            </LiButtonControlsArrow>
            <LiButtonControlsArrow>
              <Button onClick={prev}>
                <span aria-hidden="true">&lt;</span>
              </Button>
            </LiButtonControlsArrow>

            {buttons.map((item, index) => (
              <li key={index.toString()}>
                {(item === pageActual && (
                  <ButtonHighlighted>
                    <Button onClick={() => goTo(item)}>{item}</Button>
                  </ButtonHighlighted>
                )) || (
                  <ButtonNormal>
                    <Button onClick={() => goTo(item)}>{item}</Button>
                  </ButtonNormal>
                )}
              </li>
            ))}

            <LiButtonControlsArrow>
              <Button onClick={next}>
                <span aria-hidden="true">&gt;</span>
              </Button>
            </LiButtonControlsArrow>
            <LiButtonControlsArrow>
              <Button onClick={() => goTo(state.totalPages)}>
                <span aria-hidden="true">&raquo;</span>
              </Button>
            </LiButtonControlsArrow>
          </ul>
        </nav>
      </Content>
    </Container>
  );
};

export default PaginationAndSetInputsOnComponentService;
